import { ExecutionId } from "../../player/MediaResource.js";

/**
 * Represents the execution state and progress milestones for a media playback session.
 *
 * Tracks whether the session has started, an impression has occurred, and if it has completed.
 * Also manages a list of progress milestones, allowing extraction of milestones as playback progresses.
 *
 * @example
 * const info = new ExecutionInfo([25, 50, 75, 100]);
 * info.setStarted();
 * info.setImpression();
 * const milestone = info.extractProgressMilestone(30); // returns 25
 */
export class ExecutionInfo {
    private _impression: boolean;
    private _completed: boolean;
    private _started: boolean;
    
    constructor(private _milestones: number[]) {
        this._impression = false;
        this._completed = false;
        this._started = false;
    }

    
    public get started() {
        return this._started;
    }

    public setStarted() {
        this._started = true;
    }

    public get impression() {
        return this._impression;
    }
    public setImpression() {   
        this._impression = true;
    }

    public get completed() {
        return this._completed;
    }
    public setCompleted() {   
        this._completed = true;
    }

    public extractProgressMilestone(prog: number): number {
        if(this._milestones.length > 0 && this._milestones[0] <= prog) {
            return this._milestones.shift();
        }
        else return -1;
    }


}


/**
 * Manages execution data and milestone tracking for multiple executions.
 *
 * Stores execution information mapped by unique execution IDs, and provides
 * milestone-based progress tracking for each execution. Milestones are sorted
 * and can be customized via the constructor.
 *
 * @example
 * ```typescript
 * const data = new VolatileExecutionData([0, 50, 100]);
 * const info = data.getExecutionInfo('exec1');
 * ```
 *
 * @remarks
 * - Execution information is lazily initialized when requested.
 * - Milestones are sorted in ascending order.
 *
 * @public
 */
export class VolatileExecutionData {

    private executions: {[k: ExecutionId]: ExecutionInfo};
    private milestones: number[];

    constructor(milestones: number[] = [0,25,50,75,90]) {
        this.milestones = milestones.sort((a,b) => a - b)
        this.executions = {};
    }


    getExecutionInfo(id: ExecutionId): ExecutionInfo  {
        if(!(id in this.executions)) {
            this.executions[id] = new ExecutionInfo(this.milestones);
        }
        return this.executions[id];
    }

}